// src/App.js
import './assets/css/styles.css';
import './assets/css/supportButton.css'
import { Suspense, lazy } from 'react';
import { AuthProvider } from './services/AuthContext';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import ParentRoute from './components/ParentRoute'
import { NotificationContainer } from 'react-notifications';
import { Toaster } from 'react-hot-toast';
import Loader from './pages/Loader';
import { isCollegeSchool } from './components/utils/schoolFlags';

// ✅ Single source of truth
import ActivationBridge from './components/subscription/ActivationBridge';

const Signin = lazy(() => import('./pages/SigninNew'));
const SignupAccount = lazy(() => import('./pages/SignupAccount'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const EmailConfirmation = lazy(() => import('./pages/EmailConfirmation'));
const SigninManual = lazy(() => import('./pages/SigninManual'));
const Landing = lazy(() => import('./pages/Landing'));
const PublicAdmissionFormPage = lazy(() => import('./pages/admission/PublicAdmissionFormPage'));
const PublicCodeEntryPage = lazy(() => import('./pages/admission/PublicCodeEntryPage'));
const PrintableAdmissionSubmissionPage = lazy(() => import('./pages/admission/PrintableAdmissionSubmissionPage'));
const PublicPrintableAdmissionLetterPage = lazy(() => import('./pages/admission/PublicPrintableAdmissionLetterPage'));
const AdmissionLetterTemplatesPage = lazy(() => import('./pages/admission/AdmissionLetterTemplatesPage'));
const PublicClientExperienceSurveyPage = lazy(() => import('./pages/survey/PublicClientExperienceSurveyPage'));
const VerifyReport = lazy(() => import('./components/VerifyReport'));
const ResultChecker = lazy(() => import('./components/ResultChecker'));
const ApplicationForm = lazy(() => import('./components/admission/ApplicationForm'));
const CongratulationsPage = lazy(() => import('./components/CongratulationsPage'));
const UpdateContactInfo = lazy(() => import('./components/expose-form/UpdateContactInfo'));
const UpdateContactInfoEmerald = lazy(() => import('./components/expose-form/UpdateContactInfoEmerald'));
const UpdateContactInfoSunnah = lazy(() => import('./components/expose-form/UpdateContactInfoSunnah'));
const UpdateContactInfoSunnahKulliya = lazy(() => import('./components/expose-form/UpdateContactInfoSunnahKulliya'));
const UpdateContactInfoAmanah = lazy(() => import('./components/expose-form/UpdateContactInfoAmanah'));
const UpdateContactInfoAmanahTahfiz = lazy(() => import('./components/expose-form/UpdateContactInfoAmanahTahfiz'));
const UpdateContactInfoAmanahSatSun = lazy(() => import('./components/expose-form/UpdateContactInfoAmanahSatSun'));
const UpdateContactInfoAHA = lazy(() => import('./components/expose-form/UpdateContactInfoAHA'));
const UpdateContactInfoAlbyanaBauchi = lazy(() => import('./components/expose-form/UpdateContactInfoAlbyanaBauchi'));
const UpdateContactInfoALC = lazy(() => import('./components/expose-form/UpdateContactInfoALC'));

const UpdateContactInfoGreenCollege = lazy(() => import('./components/expose-form/UpdateContactInfoGreenCollege'));
const UpdateContactInfoGreenPrimary = lazy(() => import('./components/expose-form/UpdateContactInfoGreenPrimary'));
// const UpdateContactInfoSHISYanshanu = lazy(() => import('./components/expose-form/UpdateContactInfoSHISYanshanu'));

const CBTDownloadCard = lazy(() => import('./components/expose-form/CBTDownloadCard'));
const CbtInterestForm = lazy(() => import('./components/expose-form/CbtInterestForm'));
// const ParentPortal = lazy(() => import('./components/boarding-school/ParentPortal'));

const ParentPortal = lazy(() => import('./components/parent/ParentPortal'))
const ParentLogin = lazy(() => import('./components/parent/ParentLogin'))
const ParentSignupAndLink = lazy(() => import('./components/parent/ParentSignupAndLink'))
const EdusafLanding = lazy(() => import("./components/landing/EdusafLanding"))
const TeacherDashboard = lazy(() => import('./components/teacher/TeacherDashboard'))
const TeacherAssessment = lazy(() => import('./components/teacher/TeacherAssessment'))
const TeacherAttendance = lazy(() => import('./components/teacher/TeacherAttendance'))
const TeacherLessonPlan = lazy(() => import('./components/teacher/TeacherLessonPlan'))
const SchemeOfWork = lazy(() => import('./components/lesson-plan/SchemeOfWork'))

// src/App.jsx (add imports)
const LecturersDashboard = lazy(() => import("./components/lecturer/LecturersDashboard"));
const LecturerAttendance = lazy(() => import("./components/lecturer/LecturerAttendance"));
const LecturerCourses = lazy(() => import("./components/lecturer/LecturerCourses"));
const LecturerGradebook = lazy(() => import("./components/lecturer/LecturerGradebook"));
const LecturerMaterials = lazy(() => import("./components/lecturer/LecturerMaterials"));

const TeacherDashboardRoute = () =>
  isCollegeSchool() ? <Redirect to="/lecturer" /> : <TeacherDashboard />;

const TeacherAssessmentRoute = () =>
  isCollegeSchool() ? <Redirect to="/lecturer/gradebook" /> : <TeacherAssessment />;

const TeacherAttendanceRoute = () =>
  isCollegeSchool() ? <Redirect to="/lecturer/attendance" /> : <TeacherAttendance />;

const TeacherLessonPlanRoute = () =>
  isCollegeSchool() ? <Redirect to="/lecturer" /> : <TeacherLessonPlan />;

const TeacherSchemeOfWorkRoute = () =>
  isCollegeSchool() ? <Redirect to="/lecturer" /> :
    <SchemeOfWork lessonPlanPath="/lesson-plans/my" />;






function App() {
  return (
    <div>
      <Router>
        <AuthProvider>
          <Suspense fallback={<Loader />}>
            <Switch>
              {/* Public */}
              <Route exact path="/" component={EdusafLanding} />
              <Route path="/login" component={Signin} />



              <Route path="/emailconfirmation" component={EmailConfirmation} />
              <Route path="/forgotpassword" component={ForgotPassword} />
              <Route path="/manual" component={SigninManual} />
              <Route path="/v/:pstu" component={VerifyReport} />
              <Route path="/v" component={VerifyReport} />
              <Route path="/result" component={ResultChecker} />
              <Route path="/update" component={UpdateContactInfo} />
              <Route path="/emerald" component={UpdateContactInfoEmerald} />
              <Route path="/sunnah" component={UpdateContactInfoSunnah} />
              <Route path="/sunnah-academy" component={UpdateContactInfoSunnahKulliya} />
              <Route path="/amana" component={UpdateContactInfoAmanah} />
              <Route path="/amana-tahfiz" component={UpdateContactInfoAmanahTahfiz} />
              <Route path="/amana-sat-sun" component={UpdateContactInfoAmanahSatSun} />
              <Route path="/aha" component={UpdateContactInfoAHA} />
              <Route path="/alc" component={UpdateContactInfoALC} />
              <Route path="/albayan" component={UpdateContactInfoAlbyanaBauchi} />
              <Route path="/green-college" component={UpdateContactInfoGreenCollege} />
             <Route path="/green-primary" component={UpdateContactInfoGreenPrimary} />
             {/* <Route path="/shis-yanshanu" component={UpdateContactInfoSHISYanshanu} /> */}






              <Route path="/cbt" component={CbtInterestForm} />
              <Route path="/cbt-download" component={CBTDownloadCard} />


              {/* <Route path="/visit" component={ParentPortal} /> */}
              <Route
                exact
                path="/apply/:schoolPath/:formSelector/submission/:submissionId/print"
                component={PrintableAdmissionSubmissionPage}
              />
              <Route
                exact
                path="/apply/:schoolPath/submission/:submissionId/print"
                component={PrintableAdmissionSubmissionPage}
              />
              <Route
                exact
                path="/apply/:schoolPath/:formSelector/submission/:submissionId/letter"
                component={PublicPrintableAdmissionLetterPage}
              />
              <Route
                exact
                path="/apply/:schoolPath/submission/:submissionId/letter"
                component={PublicPrintableAdmissionLetterPage}
              />
              <Route exact path="/apply/:schoolPath/:formSelector/code" component={PublicCodeEntryPage} />
              <Route exact path="/apply/:schoolPath/code" component={PublicCodeEntryPage} />
              <Route exact path="/apply/:schoolPath/:formSelector" component={PublicAdmissionFormPage} />
              <Route exact path="/apply/:schoolPath" component={PublicAdmissionFormPage} />
              <Route exact path="/survey/client-experience-2026" component={PublicClientExperienceSurveyPage} />
              <Route exact path="/landing/surveys/client-experience" component={PublicClientExperienceSurveyPage} />
              <Route path="/application" component={ApplicationForm} />
              <Route path="/congratulations" component={CongratulationsPage} />

              {/* Parent auth */}
              <Route path="/parent/login" component={ParentLogin} />
              <Route path="/parent/signup" component={ParentSignupAndLink} />
              <Route
                exact
                path="/landing/result"
                render={({ location }) => (
                  <Redirect to={`/result${location?.search || ''}`} />
                )}
              />

              {/* TEACHER  */}
              <Route path="/dashboard" component={TeacherDashboardRoute} />
              <Route path="/assessments" component={TeacherAssessmentRoute} />
              <Route path="/attendance" component={TeacherAttendanceRoute} />
              <Route path="/lesson-plans/my" component={TeacherLessonPlanRoute} />
              <Route exact path="/scheme-of-work" component={TeacherSchemeOfWorkRoute} />
              <Route path="/lesson-plans/approvals" render={() => <Redirect to="/landing/lesson-plans/approvals" />} />
              <Route path="/lesson-plans/templates" render={() => <Redirect to="/landing/lesson-plans/templates" />} />
              <Route path="/lesson-plans/weeks" render={() => <Redirect to="/landing/lesson-plans/weeks" />} />
              <Route path="/lesson-plans/reports" render={() => <Redirect to="/landing/lesson-plans/reports" />} />

              <Route exact path="/lecturer" component={LecturersDashboard} />
              <Route path="/lecturer/courses" component={LecturerCourses} />
              <Route path="/lecturer/attendance" component={LecturerAttendance} />
              <Route path="/lecturer/gradebook" component={LecturerGradebook} />
              <Route path="/lecturer/materials" component={LecturerMaterials} />

              <ParentRoute path="/parent">
                <ParentPortal />
              </ParentRoute>

              <PrivateRoute exact path="/admissions/templates">
                <ActivationBridge>
                  <AdmissionLetterTemplatesPage />
                </ActivationBridge>
              </PrivateRoute>

              {/* Private */}
              <PrivateRoute path="/landing">
                <ActivationBridge>
                  <Landing />
                </ActivationBridge>
              </PrivateRoute>
            </Switch>
          </Suspense>
        </AuthProvider>
      </Router>
      <Toaster />
      <NotificationContainer />
    </div>
  );
}

export default App;
